/**
 * Transcript Storage — BYOS compliance archiving.
 *
 * Providers: azure_blob | https_webhook | aws_s3 | sharepoint
 * Scoping:   All teams (site-wide) or per-team.
 * All pushes are additive — Velaro's archive is never deleted.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { RadioDot } from "@/components/RadioOption";
import useSubscriptions from "@/hooks/useSubscriptions";
import useTeams from "@/hooks/useTeams";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import IntegrationActivityLog from "../IntegrationActivityLog";

interface StorageConfig {
  id: number;
  displayName: string;
  provider: string;
  teamId: number | null;
  isEnabled: boolean;
  format: string;
  // Azure
  azureContainerName: string;
  azurePathPrefix: string;
  azureConnectionStringSet: boolean;
  // Webhook
  webhookUrl: string;
  webhookSecretHeader: string;
  webhookSecretSet: boolean;
  // S3
  awsAccessKeyId: string;
  awsBucketName: string;
  awsRegion: string;
  awsPathPrefix: string;
  awsSecretSet: boolean;
  // SharePoint
  sharePointTenantId: string;
  sharePointClientId: string;
  sharePointSiteUrl: string;
  sharePointFolderPath: string;
  sharePointSecretSet: boolean;
  createdAt: string;
}

const emptyForm = {
  displayName: "",
  provider: "https_webhook",
  teamId: null as number | null,
  isEnabled: true,
  format: "json",
  // Azure
  azureConnectionString: "",
  azureContainerName: "",
  azurePathPrefix: "",
  // Webhook
  webhookUrl: "",
  webhookSecret: "",
  webhookSecretHeader: "",
  // S3
  awsAccessKeyId: "",
  awsSecretAccessKey: "",
  awsBucketName: "",
  awsRegion: "us-east-1",
  awsPathPrefix: "",
  // SharePoint
  sharePointTenantId: "",
  sharePointClientId: "",
  sharePointClientSecret: "",
  sharePointSiteUrl: "",
  sharePointFolderPath: ""
};

const PROVIDER_LABELS: Record<string, string> = {
  azure_blob:    "Azure Blob",
  https_webhook: "HTTPS Webhook",
  aws_s3:        "AWS S3",
  sharepoint:    "SharePoint / OneDrive"
};

const PROVIDERS = [
  { value: "https_webhook", label: "HTTPS Webhook",        sublabel: "POST to any URL",           badge: "Recommended" },
  { value: "azure_blob",    label: "Azure Blob Storage",   sublabel: "Customer's Azure account" },
  { value: "aws_s3",        label: "AWS S3",               sublabel: "Customer's S3 bucket" },
  { value: "sharepoint",    label: "SharePoint / OneDrive", sublabel: "Microsoft 365 site" }
];

export default function TranscriptStorage() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const { teams } = useTeams();

  const [m365Connected, setM365Connected] = useState(false);
  const [configs, setConfigs] = useState<StorageConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ id: number; success: boolean; message: string } | null>(null);
  const [showOtherDest, setShowOtherDest] = useState(false);

  const isEnabled = subscription?.enableTranscriptStorage ?? false;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, m365Res] = await Promise.all([
        api.admin.get("TranscriptStorage/Configs"),
        api.admin.get("MicrosoftGraph/Status").catch(() => null)
      ]);
      const data = await configRes.json();
      setConfigs(Array.isArray(data) ? data : []);
      if (m365Res) {
        const m365 = await m365Res.json();
        setM365Connected(m365?.connected === true);
      }
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setForm({ ...emptyForm });
    setEditId(null);
    setShowForm(true);
  }

  function openEdit(c: StorageConfig) {
    setForm({
      displayName: c.displayName,
      provider: c.provider,
      teamId: c.teamId,
      isEnabled: c.isEnabled,
      format: c.format,
      azureConnectionString: c.azureConnectionStringSet ? "****" : "",
      azureContainerName: c.azureContainerName ?? "",
      azurePathPrefix: c.azurePathPrefix ?? "",
      webhookUrl: c.webhookUrl ?? "",
      webhookSecret: c.webhookSecretSet ? "****" : "",
      webhookSecretHeader: c.webhookSecretHeader ?? "",
      awsAccessKeyId: c.awsAccessKeyId ?? "",
      awsSecretAccessKey: c.awsSecretSet ? "****" : "",
      awsBucketName: c.awsBucketName ?? "",
      awsRegion: c.awsRegion ?? "us-east-1",
      awsPathPrefix: c.awsPathPrefix ?? "",
      sharePointTenantId: c.sharePointTenantId ?? "",
      sharePointClientId: c.sharePointClientId ?? "",
      sharePointClientSecret: c.sharePointSecretSet ? "****" : "",
      sharePointSiteUrl: c.sharePointSiteUrl ?? "",
      sharePointFolderPath: c.sharePointFolderPath ?? ""
    });
    setEditId(c.id);
    setShowForm(true);
  }

  async function save() {
    setSaving(true);
    try {
      const body = {
        displayName: form.displayName,
        provider: form.provider,
        teamId: form.teamId || null,
        isEnabled: form.isEnabled,
        format: form.format,
        azureConnectionString: form.azureConnectionString,
        azureContainerName: form.azureContainerName,
        azurePathPrefix: form.azurePathPrefix,
        webhookUrl: form.webhookUrl,
        webhookSecret: form.webhookSecret,
        webhookSecretHeader: form.webhookSecretHeader,
        awsAccessKeyId: form.awsAccessKeyId,
        awsSecretAccessKey: form.awsSecretAccessKey,
        awsBucketName: form.awsBucketName,
        awsRegion: form.awsRegion,
        awsPathPrefix: form.awsPathPrefix,
        sharePointTenantId: form.sharePointTenantId,
        sharePointClientId: form.sharePointClientId,
        sharePointClientSecret: form.sharePointClientSecret,
        sharePointSiteUrl: form.sharePointSiteUrl,
        sharePointFolderPath: form.sharePointFolderPath
      };
      if (editId !== null) {
        await api.admin.put(`TranscriptStorage/Configs/${editId}`, body);
      } else {
        await api.admin.post("TranscriptStorage/Configs", body);
      }
      setShowForm(false);
      load();
    } catch (e) {
      console.error("Failed to save transcript storage config", e);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: number) {
    if (!confirm("Remove this storage configuration? Transcripts already pushed will not be affected.")) return;
    await api.admin.delete(`TranscriptStorage/Configs/${id}`);
    load();
  }

  async function testConnection(id: number) {
    setTestResult(null);
    try {
      const res = await api.admin.post(`TranscriptStorage/Configs/${id}/Test`, {});
      const data = await res.json();
      setTestResult({ id, success: data.success, message: data.message });
    } catch {
      setTestResult({ id, success: false, message: "Connection test failed." });
    }
  }

  function providerBadgeClass(provider: string) {
    return provider === "azure_blob"    ? "bg-blue-100 text-blue-700"
         : provider === "https_webhook" ? "bg-purple-100 text-purple-700"
         : provider === "aws_s3"        ? "bg-orange-100 text-orange-700"
         : provider === "sharepoint"    ? "bg-cyan-100 text-cyan-700"
         : "bg-slate-100 text-slate-600";
  }

  function providerSummary(c: StorageConfig) {
    if (c.provider === "azure_blob")   return `Container: ${c.azureContainerName || "—"}${c.azurePathPrefix ? " • " + c.azurePathPrefix : ""}`;
    if (c.provider === "https_webhook") return c.webhookUrl ? c.webhookUrl.replace(/^https?:\/\//, "").substring(0, 60) : "— (no URL)";
    if (c.provider === "aws_s3")       return `Bucket: ${c.awsBucketName || "—"} (${c.awsRegion || "—"})`;
    if (c.provider === "sharepoint")   return c.sharePointSiteUrl ? c.sharePointSiteUrl.replace("https://", "") : "— (no site URL)";
    return "";
  }

  if (!isEnabled) {
    return (
      <div className="p-8 max-w-2xl">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-6 py-5">
          <h2 className="text-base font-semibold text-amber-900 mb-2">Transcript Storage is not enabled</h2>
          <p className="text-sm text-amber-800">
            This is a premium compliance feature. Contact Velaro support to enable BYOS transcript archiving for your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Transcript Storage</h1>
          <p className="text-sm text-slate-500 mt-1">
            Push a copy of every resolved conversation to your own storage. Velaro's archive is never deleted — this is additive.
          </p>
        </div>
        <PrimaryBlueButton label="Add Destination" onClick={openNew} />
      </div>

      {/* Scoping info */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 mb-5 text-sm text-blue-900 space-y-1">
        <p><strong>Site-wide:</strong> Leave team blank — all conversations push to this destination.</p>
        <p><strong>Per-team:</strong> Select a team — only that team's conversations are pushed.</p>
        <p><strong>Workflow-based:</strong> Use the <strong>Push to CRM</strong> workflow node to push at a specific step.</p>
        <p><strong>File path:</strong> <code className="bg-blue-100 px-1 rounded text-xs">{"{prefix}/{year}/{month}/{day}/{conversationId}.json"}</code></p>
      </div>

      {/* Config list */}
      {loading ? (
        <p className="text-sm text-slate-400">Loading...</p>
      ) : configs.length === 0 && !showForm ? (
        <div className="rounded-lg border border-dashed border-slate-300 px-8 py-10 text-center text-slate-400 text-sm">
          No storage destinations configured. Click <strong>Add Destination</strong> to get started.
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {configs.map(c => (
            <div key={c.id} className="rounded-lg border border-slate-200 bg-white px-5 py-4 flex items-center gap-4">
              <span className={`flex-shrink-0 text-xs font-medium px-2 py-1 rounded-full ${providerBadgeClass(c.provider)}`}>
                {PROVIDER_LABELS[c.provider] ?? c.provider}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{c.displayName || "Unnamed"}</p>
                <p className="text-xs text-slate-400 truncate">
                  {providerSummary(c)}
                  {" • "}
                  {c.teamId ? `Team: ${teams.find(t => t.id === c.teamId)?.name ?? c.teamId}` : "All teams"}
                  {" • "}{c.format.toUpperCase()}
                </p>
              </div>
              {testResult?.id === c.id && (
                <span className={`text-xs flex-shrink-0 max-w-xs ${testResult.success ? "text-green-700" : "text-red-600"}`}>
                  {testResult.message}
                </span>
              )}
              <span className={`text-xs font-medium flex-shrink-0 ${c.isEnabled ? "text-green-600" : "text-slate-400"}`}>
                {c.isEnabled ? "Active" : "Disabled"}
              </span>
              <div className="flex gap-3 flex-shrink-0 text-xs">
                <button className="text-blue-600 hover:underline" onClick={() => testConnection(c.id)} aria-label="Test connection">Test</button>
                <button className="text-slate-500 hover:text-slate-800" onClick={() => openEdit(c)} aria-label="Edit">Edit</button>
                <button className="text-red-500 hover:text-red-700" onClick={() => remove(c.id)} aria-label="Remove">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5 mb-6">
          <h2 className="text-base font-semibold text-slate-800">
            {editId !== null ? "Edit Storage Destination" : "New Storage Destination"}
          </h2>

          <FormField label="Display Name" hint="E.g. 'HIPAA Archive – Support Team'">
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={form.displayName}
              onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
              placeholder="HIPAA Archive – Support Team"
            />
          </FormField>

          {/* Provider selector */}
          <FormField label="Provider">
            <div className="flex flex-wrap gap-3">
              {PROVIDERS.map(p => (
                <label key={p.value} className={`flex flex-col px-4 py-3 rounded-lg border cursor-pointer text-sm w-44
                  ${form.provider === p.value ? "border-blue-500 bg-blue-50" : "border-slate-300"}`}>
                  <span className="flex items-center gap-2">
                    <input type="radio" name="provider" value={p.value} checked={form.provider === p.value} onChange={() => setForm(f => ({ ...f, provider: p.value }))} className="sr-only" />
                    <RadioDot checked={form.provider === p.value} />
                    <span className={`font-medium ${form.provider === p.value ? "text-blue-800" : "text-slate-700"}`}>{p.label}</span>
                  </span>
                  <span className="text-xs text-slate-400 mt-1 ml-5">{p.sublabel}</span>
                  {p.badge && <span className="text-xs text-green-600 font-medium mt-0.5 ml-5">{p.badge}</span>}
                </label>
              ))}
            </div>
          </FormField>

          {/* ── HTTPS Webhook ── */}
          {form.provider === "https_webhook" && (
            <>
              <div className="rounded-lg bg-purple-50 border border-purple-200 px-4 py-3 text-sm text-purple-800 space-y-1">
                <p className="font-medium">How the webhook works</p>
                <p>Velaro sends HTTP POST to your URL with the transcript as the body and these headers:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5 mt-1">
                  <li><code>X-Velaro-Secret</code> — your secret for verification</li>
                  <li><code>X-Velaro-SiteId</code>, <code>X-Velaro-ConversationId</code>, <code>X-Velaro-Event: conversation.resolved</code></li>
                </ul>
                <p className="text-xs mt-2">
                  <strong>AWS S3 via Lambda:</strong> Deploy a Lambda with a Function URL — receive POST → <code>s3.putObject()</code>.<br />
                  <strong>Azure Functions:</strong> HTTP trigger → blob SDK upload.<br />
                  <strong>Power Automate:</strong> HTTP trigger flow — no code required.<br />
                  <strong>SFTP / other:</strong> Any server that can receive HTTPS can store to SFTP, a database, or anything else.
                </p>
              </div>

              <FormField label="Webhook URL" hint="Must be HTTPS. Velaro POSTs the transcript JSON (or TXT) as the body.">
                <input
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.webhookUrl}
                  onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))}
                  placeholder="https://your-endpoint.example.com/velaro-transcripts"
                />
              </FormField>

              <FormField label="Secret (optional)" hint="Sent in X-Velaro-Secret header so your endpoint can verify the request is from Velaro. Recommend 32+ random characters.">
                <input
                  type="password"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.webhookSecret}
                  onChange={e => setForm(f => ({ ...f, webhookSecret: e.target.value }))}
                  placeholder="Generate a random 32+ character string"
                />
              </FormField>

              <FormField
                label="Secret Header Name (optional)"
                hint={<>Leave blank for <code>X-Velaro-Secret</code>. Use <code>Authorization</code> to send as <code>Bearer {"{secret}"}</code>, or <code>X-Api-Key</code> for API gateway auth.</>}
              >
                <input
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.webhookSecretHeader}
                  onChange={e => setForm(f => ({ ...f, webhookSecretHeader: e.target.value }))}
                  placeholder="X-Velaro-Secret"
                />
              </FormField>
            </>
          )}

          {/* ── Azure Blob ── */}
          {form.provider === "azure_blob" && (
            <>
              <FormField
                label="Connection String"
                hint={<><strong>Azure Portal → Storage Accounts → [account] → Access Keys → Connection string</strong>. Starts with <code>DefaultEndpointsProtocol=https;...</code></>}
              >
                <input
                  type="password"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.azureConnectionString}
                  onChange={e => setForm(f => ({ ...f, azureConnectionString: e.target.value }))}
                  placeholder="DefaultEndpointsProtocol=https;AccountName=...;AccountKey=..."
                />
              </FormField>
              <FormField label="Container Name" hint="Lowercase, letters/numbers/hyphens only. Created automatically if it doesn't exist.">
                <input
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.azureContainerName}
                  onChange={e => setForm(f => ({ ...f, azureContainerName: e.target.value }))}
                  placeholder="velaro-transcripts"
                />
              </FormField>
              <FormField label="Path Prefix (optional)" hint={<>Files stored as: <code className="text-xs bg-slate-100 px-1 rounded">{`{prefix}/{year}/{month}/{day}/{id}.json`}</code></>}>
                <input
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.azurePathPrefix}
                  onChange={e => setForm(f => ({ ...f, azurePathPrefix: e.target.value }))}
                  placeholder="support-team/"
                />
              </FormField>
            </>
          )}

          {/* ── AWS S3 ── */}
          {form.provider === "aws_s3" && (
            <>
              <div className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-800 space-y-1">
                <p className="font-medium">IAM setup</p>
                <p className="text-xs">
                  Create an IAM user with <strong>programmatic access</strong>. Attach a policy with <code>s3:PutObject</code> on your bucket.
                  Recommended minimal policy:<br />
                  <code className="block mt-1 bg-orange-100 rounded px-2 py-1 text-xs whitespace-pre">{`{ "Effect": "Allow", "Action": "s3:PutObject", "Resource": "arn:aws:s3:::YOUR-BUCKET/*" }`}</code>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Access Key ID">
                  <input
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.awsAccessKeyId}
                    onChange={e => setForm(f => ({ ...f, awsAccessKeyId: e.target.value }))}
                    placeholder="AKIAIOSFODNN7EXAMPLE"
                  />
                </FormField>
                <FormField label="Secret Access Key">
                  <input
                    type="password"
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.awsSecretAccessKey}
                    onChange={e => setForm(f => ({ ...f, awsSecretAccessKey: e.target.value }))}
                    placeholder="wJalrXUtnFEMI/K7MDENG/..."
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Bucket Name">
                  <input
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.awsBucketName}
                    onChange={e => setForm(f => ({ ...f, awsBucketName: e.target.value }))}
                    placeholder="my-company-transcripts"
                  />
                </FormField>
                <FormField label="Region">
                  <select
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.awsRegion}
                    onChange={e => setForm(f => ({ ...f, awsRegion: e.target.value }))}
                  >
                    {["us-east-1","us-east-2","us-west-1","us-west-2","eu-west-1","eu-west-2","eu-west-3","eu-central-1","ap-southeast-1","ap-southeast-2","ap-northeast-1","ca-central-1","sa-east-1"].map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </FormField>
              </div>
              <FormField label="Key Prefix (optional)" hint={<>Files stored as: <code className="text-xs bg-slate-100 px-1 rounded">{`{prefix}/{year}/{month}/{day}/{id}.json`}</code></>}>
                <input
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.awsPathPrefix}
                  onChange={e => setForm(f => ({ ...f, awsPathPrefix: e.target.value }))}
                  placeholder="velaro/transcripts/"
                />
              </FormField>
            </>
          )}

          {/* ── SharePoint / OneDrive ── */}
          {form.provider === "sharepoint" && (
            m365Connected ? (
              <>
                <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
                  ✓ <strong>Microsoft 365 connected.</strong> Just enter your SharePoint site URL and folder — no credentials needed.
                </div>
                <FormField label="SharePoint Site URL" hint="Paste the URL of your SharePoint site. Browse available sites in the Microsoft 365 integration page.">
                  <input
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.sharePointSiteUrl}
                    onChange={e => setForm(f => ({ ...f, sharePointSiteUrl: e.target.value }))}
                    placeholder="https://contoso.sharepoint.com/sites/compliance"
                  />
                </FormField>
                <FormField label="Folder Path (optional)" hint={<>Folder within the document library. Files: <code className="text-xs bg-slate-100 px-1 rounded">{`{folder}/{year}/{month}/{day}/{id}.json`}</code></>}>
                  <input
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.sharePointFolderPath}
                    onChange={e => setForm(f => ({ ...f, sharePointFolderPath: e.target.value }))}
                    placeholder="Velaro Transcripts"
                  />
                </FormField>
              </>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                <p className="font-medium mb-1">Connect Microsoft 365 first</p>
                <p className="text-xs mb-3">SharePoint storage requires a one-time Microsoft 365 connection. It takes about 30 seconds — just click Connect and approve on Microsoft's screen.</p>
                <Link to="/integrations/microsoft-365" className="inline-block text-xs font-medium text-blue-700 underline">
                  Go to Microsoft 365 → Connect
                </Link>
              </div>
            )
          )}

          {/* Scope */}
          <FormField label="Apply to" hint="Scope to a specific team, or leave blank to push all teams' transcripts.">
            <select
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={form.teamId ?? ""}
              onChange={e => setForm(f => ({ ...f, teamId: e.target.value ? Number(e.target.value) : null }))}
            >
              <option value="">All teams (site-wide)</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </FormField>

          {/* Format */}
          <FormField label="Format" hint="JSON includes all metadata. TXT is plain text for human review.">
            <div className="flex gap-3">
              {[["json", "JSON (structured)"], ["txt", "TXT (plain text)"]] .map(([val, label]) => (
                <label key={val} className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm
                  ${form.format === val ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-300 text-slate-600"}`}>
                  <input type="radio" name="format" value={val} checked={form.format === val} onChange={() => setForm(f => ({ ...f, format: val }))} className="sr-only" />
                  <RadioDot checked={form.format === val} />
                  {label}
                </label>
              ))}
            </div>
          </FormField>

          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.isEnabled}
              onChange={e => setForm(f => ({ ...f, isEnabled: e.target.checked }))}
              className="h-4 w-4 accent-blue-500" />
            <span className="text-sm text-slate-700">Enable this destination</span>
          </label>

          <div className="flex gap-3 pt-2">
            <PrimaryBlueButton
              label={saving ? "Saving..." : (editId !== null ? "Save Changes" : "Add Destination")}
              onClick={save}
              disabled={saving}
            />
            <SecondaryBlueButton label="Cancel" onClick={() => setShowForm(false)} />
          </div>
        </div>
      )}

      {/* Other destinations / webhook guide */}
      <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100 text-left"
          onClick={() => setShowOtherDest(v => !v)}
          aria-expanded={showOtherDest}
        >
          <span>Need a different destination? (SFTP, Google Cloud, Dropbox, databases...)</span>
          <span className="text-slate-400 text-xs">{showOtherDest ? "▲" : "▼"}</span>
        </button>

        {showOtherDest && (
          <div className="px-5 pb-5 pt-1 text-sm text-slate-600 space-y-4 border-t border-slate-200">
            <p>
              Use the <strong>HTTPS Webhook</strong> provider to push to any destination your own server or serverless function can reach.
              The webhook sends the full transcript as a JSON POST — your code handles the final delivery.
            </p>

            <div className="space-y-3">
              <DestExample
                title="SFTP"
                body="Run a small server (AWS Lambda, Azure Function, or a VM) that receives the webhook POST and writes the file to your SFTP server using an SSH library. Example (Node.js): receive POST → ssh2-sftp-client.put(buffer, remotePath)."
              />
              <DestExample
                title="Google Cloud Storage"
                body="Deploy a Cloud Function with an HTTP trigger. On POST, call storage.bucket(name).file(path).save(body) using the @google-cloud/storage SDK. The webhook URL is the Cloud Function trigger URL."
              />
              <DestExample
                title="Dropbox / Box"
                body="Use Power Automate or Zapier with an HTTP trigger. Connect the action to Dropbox 'Create file' or Box 'Upload file'. No code required — paste the Power Automate webhook URL into the Webhook URL field above."
              />
              <DestExample
                title="SQL / NoSQL database"
                body="Deploy any HTTPS endpoint (Lambda, Azure Function, your own API) that receives the transcript JSON and inserts it into your database. Add your DB credentials to the function — Velaro never needs to know about them."
              />
              <DestExample
                title="Slack / Teams notification"
                body="Use a Slack Incoming Webhook URL or a Power Automate flow with a Teams action. Post a summary of the conversation to your compliance or ops channel on every resolve."
              />
            </div>

            <p className="text-xs text-slate-400 mt-2">
              The webhook sends <code>X-Velaro-Secret</code>, <code>X-Velaro-SiteId</code>, <code>X-Velaro-ConversationId</code>, and <code>X-Velaro-Event: conversation.resolved</code> headers so your endpoint can authenticate and route requests without parsing the body.
            </p>
          </div>
        )}
      </div>

      {/* Compliance note */}
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
        <p className="font-medium text-slate-700 mb-1">Compliance Note</p>
        <p>
          Velaro keeps all transcripts in its own archive permanently. This feature gives you an additional copy in storage
          you own — for HIPAA, SOC 2, or internal retention policies where data must reside in your infrastructure.
          Set your own retention policy in Azure, AWS, SharePoint, or your endpoint.
        </p>
      </div>

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="TranscriptStorage" />
      </div>
    </div>
  );
}

function FormField({ label, hint, children }: { label: string; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function DestExample({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-sm font-medium text-slate-700 mb-1">{title}</p>
      <p className="text-xs text-slate-500">{body}</p>
    </div>
  );
}
